/**
 * Report Template Service
 */

import { api } from '../../lib/api'
import { API_ROUTES } from '../../config/constants'
import type {
  ReportTemplate,
  CreateTemplateData,
  UpdateTemplateData,
  TemplateFilters,
  TemplateSearchResult,
} from '../../domain/report-template/types'

export const templateService = {
  /**
   * Search templates with filters
   */
  search: async (filters?: TemplateFilters): Promise<TemplateSearchResult> => {
    const params = new URLSearchParams()
    if (filters?.institutionId !== undefined) {
      params.set('institutionId', filters.institutionId === null ? 'global' : filters.institutionId)
    }
    if (filters?.modalityCode) params.set('modalityCode', filters.modalityCode)
    if (filters?.category) params.set('category', filters.category)
    if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
    if (filters?.search) params.set('search', filters.search)
    if (filters?.limit) params.set('limit', String(filters.limit))
    if (filters?.offset) params.set('offset', String(filters.offset))

    const url = params.toString()
      ? `${API_ROUTES.REPORT_TEMPLATES.BASE}?${params.toString()}`
      : API_ROUTES.REPORT_TEMPLATES.BASE

    const response = await api.get(url)
    return response.data
  },

  /**
   * Get template by ID
   */
  getById: async (id: string): Promise<{ template: ReportTemplate }> => {
    const response = await api.get(API_ROUTES.REPORT_TEMPLATES.BY_ID(id))
    return response.data
  },

  /**
   * Get templates by modality (for viewer dropdown)
   */
  getByModality: async (modalityCode: string): Promise<{ templates: ReportTemplate[] }> => {
    const response = await api.get(API_ROUTES.REPORT_TEMPLATES.BY_MODALITY(modalityCode))
    return response.data
  },

  /**
   * Get templates by institution
   */
  getByInstitution: async (institutionId: string | null): Promise<{ templates: ReportTemplate[] }> => {
    const id = institutionId ?? 'global'
    const response = await api.get(API_ROUTES.REPORT_TEMPLATES.BY_INSTITUTION(id))
    return response.data
  },

  /**
   * Create new template
   */
  create: async (data: CreateTemplateData): Promise<{ message: string; template: ReportTemplate }> => {
    const response = await api.post(API_ROUTES.REPORT_TEMPLATES.BASE, data)
    return response.data
  },

  /**
   * Update template
   * Using POST instead of PUT for WAF compatibility
   */
  update: async (id: string, data: UpdateTemplateData): Promise<{ message: string; template: ReportTemplate }> => {
    const response = await api.post(API_ROUTES.REPORT_TEMPLATES.UPDATE(id), data)
    return response.data
  },

  /**
   * Delete template
   * Using POST instead of DELETE for WAF compatibility
   */
  delete: async (id: string): Promise<void> => {
    await api.post(API_ROUTES.REPORT_TEMPLATES.DELETE(id))
  },
}
